import { AccountingService } from '@accounting/core/accounting/accounting.service';
import { InvoiceService } from '@accounting/core/accounting/invoice-service/invoice.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { PracticePayRefundTransactionResponse } from '@gandalf/model/practice-pay-refund-transaction-response';
import { DialogUtil, ModalBase, ModalManagerService } from 'morgana';
import { PracticePayPaymentService } from '@core/practice-pay-payment/practice-pay-payment.service';
import { VoidPaymentRequest } from '@gandalf/model/void-payment-request';
import { PaymentMethodCreditCardType } from '@gandalf/constants';
import { DialogComponent } from '@syncfusion/ej2-angular-popups';
import { GandalfFormBuilder, TypedFormGroup } from 'gandalf';

export interface PracticePayVoidPaymentModalData {
	invoiceId: number;
	paymentId: number;
	payerName: string;
	paymentMethodCardType: PaymentMethodCreditCardType;
	paymentAmount: number;
	last4CreditCard: string;
}

const UNHANDLED_ERROR_TEXT = 'An Error has Occurred. Select "Retry" to continue.';
const ERROR_AFTER_RECEIVED_TEXT = 'An Error has Occurred. The void was processed by RevPay, however the void was not applied within RevolutionEHR.' +
	' Please void manually to complete the transaction.';
const REFUND_RESULT_TEXT = 'The payment was voided successfully. The cardholder will see a charge posted to their account,' +
	' which will be refunded in 3-5 business days.';

@Component({
	selector: 'pms-practice-pay-void-payment-modal',
	templateUrl: './practice-pay-void-payment-modal.component.html',
	providers: [ModalManagerService],
	styles: [],
	standalone: false,
})
export class PracticePayVoidPaymentModalComponent extends ModalBase<PracticePayVoidPaymentModalData, boolean> implements OnInit {

	@ViewChild('modal')
	modal: DialogComponent;

	@ViewChild('templateForm')
	templateForm: NgForm;

	componentForm: TypedFormGroup<VoidPaymentRequest>;
	voidPaymentRequest: VoidPaymentRequest;
	isVoiding = false;
	errorMessage: string;

	constructor(
		private gandalfFormBuilder: GandalfFormBuilder,
		private practicePayPaymentService: PracticePayPaymentService,
		private accountingService: AccountingService,
		private invoiceService: InvoiceService,
	) {
		super();
	}

	ngOnInit() {
		this.createForm();
	}

	createForm() {
		this.voidPaymentRequest = new VoidPaymentRequest();
		this.voidPaymentRequest.paymentId = this.data.paymentId;
		this.voidPaymentRequest.comments = '';
		this.componentForm = this.gandalfFormBuilder.groupTyped(this.voidPaymentRequest);
	}

	/* istanbul ignore next: closeModal */
	closeModal(manualVoidProcessRequested: boolean) {
		this.dynamicModalRef.close(this.modal, manualVoidProcessRequested);
	}

	voidPayment() {
		if (this.componentForm.invalid) {
			return;
		}

		this.errorMessage = null;
		this.isVoiding = true;
		this.practicePayPaymentService.refundSaleTransaction(this.data.paymentId).subscribe({
			next: response => this.handlePracticePayRefundResponse(response),
			error: () => this.displayError(UNHANDLED_ERROR_TEXT),
		});
	}

	handlePracticePayRefundResponse(response: PracticePayRefundTransactionResponse) {
		const request = this.componentForm.value as VoidPaymentRequest;
		if (response.refundReceived) {
			request.paymentTransactionId = response.paymentTransactionId;
			this.accountingService.voidPayment(request)
				.subscribe({
					next: () => this.onVoidSuccess(),
					error: () => this.displayError(ERROR_AFTER_RECEIVED_TEXT),
				});
		} else {
			this.displayError(`Error: ${response.errorMessage}`);
		}
	}

	displayError(errorMessage: string) {
		this.errorMessage = errorMessage;
		this.isVoiding = false;
	}

	onVoidSuccess() {
		this.invoiceService.refreshInvoice(this.data.invoiceId);
		this.closeModal(false);
		DialogUtil.alert({
			title: 'Successfully Processed',
			content: REFUND_RESULT_TEXT,
		});
	}

	get primaryButtonText() {
		return this.errorMessage ? 'Retry' : 'Void';
	}
}
